import React, { useState } from "react";
import "./ProductCard.css";

// Helper function to decode HTML entities and strip HTML tags
const decodeHTMLEntities = (text) => {
  if (!text) return "";

  // Create a temporary element to decode HTML entities
  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;
  const decoded = textarea.value;

  // Remove any HTML tags
  const cleaned = decoded.replace(/<[^>]+>/g, "");

  return cleaned;
};

// Helper: extract the single variant from a product's variations, if there's
// exactly one variant and no option sets.  Returns the variant object or null.
const getSingleVariant = (product) => {
  const variationGroups = Array.isArray(product?.variations) ? product.variations : [];
  const variantGroup = variationGroups.find(g => g?.isVariantGroup);
  if (variantGroup && (variantGroup.options?.length || 0) === 1) {
    return variantGroup.options[0];
  }
  return null;
};

export default function ProductCard({
  product,
  tags,
  onClick,
  onAddToCart,
  onDecreaseFromCart,
  cartLineCount = 0,
  cartQuantity = 0,
  highlightText,
  searchTerm,
  isSoldOut = false,
  isViewOnly = false,
}) {
  const [showFullDetails, setShowFullDetails] = useState(false);
  const productTags = Array.isArray(product.tags) ? product.tags : [];

  const cleanDescription = decodeHTMLEntities(product.description);

  const toggleDetails = () => setShowFullDetails((prev) => !prev);

  const fallbackImage = "https://placehold.co/400x400?text=No+Image&font=roboto";
  const imageSrc =
    product.imageUrl || product.productDefaultImage || fallbackImage;

  const hasConfigurableOptions = (() => {
    const variationGroups = Array.isArray(product?.variations) ? product.variations : [];

    const hasMultipleVariants = variationGroups.some(
      (group) => group?.isVariantGroup && (group?.options?.length || 0) > 1
    );

    const hasOptionSetsOnProduct = variationGroups.some(
      (group) => !group?.isVariantGroup && (group?.options?.length || 0) > 0
    );

    const hasOptionSetsOnVariants = variationGroups.some(
      (group) => group?.isVariantGroup
        && (group?.options || []).some(
          (variant) => Array.isArray(variant?.optionSets)
            && variant.optionSets.some((set) => (set?.options?.length || 0) > 0)
        )
    );

    return hasMultipleVariants || hasOptionSetsOnProduct || hasOptionSetsOnVariants;
  })();

  const handleQuickAdd = (e) => {
    e.stopPropagation();
    if (hasConfigurableOptions) {
      onClick(product, { source: 'add' });
      return;
    }
    if (isSoldOut || !onAddToCart) {
      return;
    }
    // Pass the single variant (if any) so the cart gets the correct skuCode
    const singleVariant = getSingleVariant(product);
    onAddToCart(product, singleVariant);
  };

  const handleQuickDecrease = (e) => {
    e.stopPropagation();
    if (isSoldOut || !onDecreaseFromCart) {
      return;
    }
    if (hasConfigurableOptions && cartLineCount > 1) {
      onClick(product, { source: 'customization' });
      return;
    }
    onDecreaseFromCart(product);
  };

// We only toggle description now, not nutrition facts
// No longer truncating in JS - using CSS ellipsis instead
  // Tapping anywhere on the card (name, image, description) opens the detail
  // view so the user can see the product and read its description. Only the
  // explicit ADD / quantity buttons add directly to the cart.
  const handleCardClick = () => {
    onClick(product);
  };

  const combinedText = cleanDescription;
  return (
    <div className={`product-card ${isSoldOut ? 'sold-out' : ''}`} onClick={handleCardClick}>
      {isSoldOut && <div className="sold-out-badge-floating">Sold Out</div>}
      <div className="product-left">
        {(() => {
          // Check if product has both egg and non-veg tags
          const hasEggTag = productTags.some(tag =>
            tag?.name?.toLowerCase().includes('egg')
          );
          const hasNonVegTag = productTags.some(tag => {
            const name = tag?.name?.toLowerCase() || '';
            return name.includes('non') && name.includes('veg');
          });
          const hasNewTag = productTags.some(tag => {
            const name = tag?.name?.toLowerCase() || '';
            return name.includes('new');
          });

          const hasEggAndNonVeg = hasEggTag && hasNonVegTag;

          // If product has both egg+non-veg AND new tag, show both
          if (hasEggAndNonVeg || hasNewTag) {
            return (
              <div className="grouped-tags">
              {hasEggTag ? 
              <div
                className="egg-nonveg-container"
                style={{ display: "flex", gap: 6, alignItems: "center" }}
              >
                <img
                  key="egg-tag"
                  src="/EggTag.png"
                  alt="Egg (Non-Veg)"
                  className="egg-nonveg-icon"
                />
              </div> : ''}
              {productTags.map((tag, index) => {
                const tagKey = tag?.id || tag?.name || index;
                if(hasEggAndNonVeg && (tag?.name?.toLowerCase() === 'egg' || tag?.name?.toLowerCase().includes('non'))){
                  return null;
                }
                else if (tag?.id === '6932636dec4624b7afca0266') {
                  return (
                    <div
                      key={tagKey}
                      className="egg-nonveg-container"
                      style={{ display: "flex", gap: 0, alignItems: "center" }}
                    >
                      <img
                        src="/NewTag.png"
                        alt="Newly Launched"
                        className="egg-nonveg-icon"
                      />
                    </div>
                  );
                }
                else if (tag?.tagIcon) {
                  return (
                    <div
              key={tagKey}
              className="tag-icon-container"
              style={{ display: "flex", gap: 6, alignItems: "center", marginBottom:0 }}
            >
                    <img
                      src={tag?.tagIcon?.fullPath}
                      alt={tag?.name}
                      className="veg-icon"
                    />
                    </div>
                  );
                }
                return null;
              })}

              </div>
            );
          }

          // Show tags normally, but replace New tag icon with NewTag.png from public folder
          return (
            <div
              className="tag-icon-container"
              style={{ display: "flex", gap: 6, alignItems: "center" }}
            >
              {productTags.map((tag, index) => {
                if (tag?.tagIcon) {
                  return (
                    <img
                      key={index}
                      src={tag?.tagIcon?.fullPath}
                      alt={tag?.name}
                      className="veg-icon"
                    />
                  );
                }
                return null;
              })}
            </div>
          );
        })()}

        <h4 className="product-name">
          {highlightText && searchTerm ? highlightText(product.name, searchTerm) : product.name}
        </h4>
        <h6 className="product-price">₹{Math.round(product.price)}</h6>
        {cleanDescription && (
          <p className={`product-description ${!showFullDetails ? "truncate" : ""}`}>
            {highlightText && searchTerm ? highlightText(combinedText, searchTerm) : combinedText}
          </p>
        )}
        {(cleanDescription.length > 200) && (
            <p
              className="more-link"
              onClick={(e) => {
                e.stopPropagation();
                toggleDetails();
              }}
            >
              {showFullDetails ? "Show Less" : "More"}
            </p>
          )}
      </div>

      <div className="product-right">
        <img src={imageSrc} alt={product.name} className="product-image" />
        {!isViewOnly &&
          (cartQuantity > 0 ? (
            <div className="quick-qty-controls" onClick={(e) => e.stopPropagation()}>
              <button
                className="quick-qty-btn"
                onClick={handleQuickDecrease}
                disabled={isSoldOut}
                aria-label={`Remove one ${product.name} from cart`}
              >
                −
              </button>
              <span className="quick-qty-value">{cartQuantity}</span>
              <button
                className="quick-qty-btn"
                onClick={handleQuickAdd}
                disabled={isSoldOut}
                aria-label={`Add one more ${product.name} to cart`}
              >
                +
              </button>
            </div>
          ) : (
            <button
              className="quick-add-btn"
              onClick={handleQuickAdd}
              disabled={isSoldOut}
              aria-label={`Add ${product.name} to cart`}
            >
              ADD
            </button>
          ))}
        {isSoldOut && <div className="sold-out-overlay"></div>}
      </div>
    </div>
  );
}
